import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Mic, MicOff, Send, Trash2, Square, Camera } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { isNativeBuild } from "@/lib/config";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";

// All accent colors now come from CSS custom properties via the palette system.

interface MessageInputProps {
    onSendMessage: (content: string, type?: string, attachments?: any[]) => void;
    onStartTyping: () => void;
    onStopTyping: () => void;
    disabled?: boolean;
    userLanguage?: string;
    roomId?: string;
    senderId?: string;
    selectedFiles?: File[];
    onFilesSelected?: (files: File[]) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

export function MessageInput({ onSendMessage, onStartTyping, onStopTyping, disabled = false, userLanguage = "en", roomId, senderId, selectedFiles: externalSelectedFiles, onFilesSelected, onFocus, onBlur }: MessageInputProps) {
    const [message, setMessage] = useState("");
    const [internalSelectedFiles, setInternalSelectedFiles] = useState<File[]>([]);
    const selectedFiles = externalSelectedFiles || internalSelectedFiles;
    const setSelectedFiles = onFilesSelected || setInternalSelectedFiles;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const typingTimeoutRef = useRef<NodeJS.Timeout>();
    const { uploadFiles, isUploading, uploadProgress, error: uploadError } = useFileUpload();
    const voice = useVoiceRecorder();

    const handleInputChange = (value: string) => {
        setMessage(value);
        if (value.trim()) {
            onStartTyping();
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => onStopTyping(), 1000);
        } else {
            onStopTyping();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSendMessage = async () => {
        if ((!message.trim() && selectedFiles.length === 0) || disabled || isUploading) return;

        try {
            let attachments: any[] = [];
            const hasAttachments = selectedFiles.length > 0;

            if (hasAttachments) {
                attachments = await uploadFiles(selectedFiles);
            }

            const content = message.trim() || (hasAttachments ? " " : "");
            onSendMessage(content, hasAttachments ? "file" : "text", attachments);

            setMessage("");
            setSelectedFiles([]);
            onStopTyping();
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

            requestAnimationFrame(() => {
                textareaRef.current?.focus();
                if (onFocus && hasAttachments) setTimeout(onFocus, 200);
            });
        } catch (error) {
            console.error("Failed to send message:", error);
        }
    };

    const handleSendVoice = async () => {
        if (!voice.audioBlob) return;

        try {
            const ext = voice.audioBlob.type.includes("webm") ? "webm" : "mp4";
            const file = new File([voice.audioBlob], `voice-message.${ext}`, {
                type: voice.audioBlob.type,
            });

            const attachments = await uploadFiles([file]);

            onSendMessage("", "voice", attachments);
            voice.clearAudio();

            requestAnimationFrame(() => textareaRef.current?.focus());
        } catch (error) {
            console.error("Failed to send voice message:", error);
        }
    };

    const handleFileAttach = () => fileInputRef.current?.click();

    /**
     * Take a photo with the device camera and stage it like any other
     * attachment. Native only — on the web the file input already offers the
     * camera on mobile browsers, and there is nothing to add.
     *
     * The photo is converted to a File and handed to the same
     * `selectedFiles` path as `handleFileSelect`, so it flows through the
     * existing upload and send logic unchanged.
     */
    const handleTakePhoto = async () => {
        try {
            const { Camera, CameraResultType, CameraSource } = await import(
                "@capacitor/camera"
            );
            const photo = await Camera.getPhoto({
                quality: 80,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                saveToGallery: false,
            });

            if (!photo.webPath) return;

            const blob = await fetch(photo.webPath).then((r) => r.blob());
            const ext = photo.format || "jpeg";
            const file = new File([blob], `photo-${Date.now()}.${ext}`, {
                type: blob.type || `image/${ext}`,
            });

            // Not a functional update: setSelectedFiles may be the parent's
            // plain onFilesSelected callback, which takes a value, not an updater.
            setSelectedFiles([...selectedFiles, file].slice(0, 5));
        } catch (error) {
            // Cancelling the camera rejects, and so does a denied permission.
            // Neither warrants interrupting the user mid-conversation.
            console.log("Photo capture cancelled or unavailable");
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const validFiles = files
            .filter((file) => {
                if (file.size > 2 * 1024 * 1024 * 1024) {
                    alert(`File ${file.name} is too large. Maximum size is 2 GB.`);
                    return false;
                }
                return true;
            })
            .slice(0, 5);
        setSelectedFiles(validFiles);
        e.target.value = "";
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 128) + "px";
        }
    }, [message]);

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        };
    }, []);

    // Auto-send when recording stops and blob is ready
    useEffect(() => {
        if (voice.audioBlob && !voice.isRecording) {
            handleSendVoice();
        }
    }, [voice.audioBlob]);

    const showSendButton = message.trim().length > 0 || selectedFiles.length > 0;
    const hasError = uploadError || voice.error;

    // --- Recording UI ---
    if (voice.isRecording) {
        return (
            <div className="bg-transparent dark:bg-transparent">
                <div className="flex items-center space-x-2 pb-6 px-2">
                    <div className="flex-1 flex items-center bg-[var(--accent)/10] border border-[var(--accent)] rounded-2xl px-4 py-1 space-x-3">
                        <div className="w-3 h-3 rounded-full bg-[var(--accent)] animate-pulse flex-shrink-0" />
                        <span className="text-sm font-medium text-white dark:text-white tabular-nums">
                            {voice.formatDuration(voice.duration)}
                        </span>

                        <div className="flex-1 flex items-center justify-center space-x-1">
                            {Array.from({ length: 20 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="w-1 bg-[var(--accent)] dark:bg-[var(--accent)] rounded-full animate-pulse"
                                    style={{
                                        height: `${8 + Math.sin((Date.now() / 200) + i) * 8}px`,
                                        animationDelay: `${i * 50}ms`,
                                    }}
                                />
                            ))}
                        </div>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={voice.cancelRecording}
                            className="text-red-500 hover:text-red-700 hover:bg-red-500/10 rounded-full flex-shrink-0"
                        >
                            <Trash2 className="w-5 h-5" />
                        </Button>
                    </div>

                    <Button
                        size="icon"
                        onClick={voice.stopRecording}
                        className="text-white rounded-full w-11 h-11 flex-shrink-0" style={{ background: 'var(--accent)' }}
                    >
                        <Send className="w-5 h-5" />
                    </Button>
                </div>
            </div>
        );
    }

    // --- Normal input UI ---
    return (
        <div className="bg-transparent dark:bg-transparent">
            <div className="flex items-start space-x-1 py-1 mx-4">
                <div
                    className="
                        flex-1 relative
                        bg-white dark:bg-[#0F172A]
                        border border-gray-300 dark:border-gray-700
                        rounded-3xl px-4 py-1.5 
                        focus-within:outline-none
                        focus-within:border-[var(--accent)]
                        focus-within:ring-1 focus-within:ring-[var(--accent)]
                        hover:border-[var(--accent)] focus-within:shadow-[6px_6px_0_0_var(--accent)]
                        transition-colors
                        flex items-end space-x-2 mb-4"
                >
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleFileAttach}
                        className="text-muted-foreground text-[#FF803E] flex-shrink-0 p-2  dark:bg-[#0f172a]/20 bg-transparent   rounded-full hover:bg-transparent backdrop-blur-[12px]  flex items-center justify-center transition-all duration-300 hover:shadow-[2px_2px_0_0_#ff803e] self-end hover:text-[#ff803e]"
                    >
                        <Paperclip className="w-2 h-2" />
                    </Button>

                    {/* Native only: the web file input already surfaces the
                        camera on mobile browsers, so a second button there
                        would just be a duplicate. */}
                    {isNativeBuild && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleTakePhoto}
                            aria-label="Take photo"
                            className="text-muted-foreground text-[#FF803E] flex-shrink-0 p-2  dark:bg-[#0f172a]/20 bg-transparent   rounded-full hover:bg-transparent backdrop-blur-[12px]  flex items-center justify-center transition-all duration-300 hover:shadow-[2px_2px_0_0_#ff803e] self-end hover:text-[#ff803e]"
                        >
                            <Camera className="w-2 h-2" />
                        </Button>
                    )}

                    <Textarea
                        ref={textareaRef}
                        placeholder="Type your message..."
                        value={message}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        disabled={disabled}
                        rows={1}
                        className="
                            flex-1 resize-none
                            bg-transparent border-none
                            focus:outline-none
                            text-base sm:text-sm text-gray-900 dark:text-gray-100
                            px-1 py-2.5
                            min-h-[40px] max-h-32
                            leading-tight
                            overflow-hidden
                            align-center
                        "
                    />

                    {showSendButton ? (
                        <Button
                            onClick={handleSendMessage}
                            disabled={(!message.trim() && selectedFiles.length === 0) || disabled || isUploading}
                            className="text-muted-foreground hover:bg-[var(--accent)] hover:text-white flex-shrink-0 rounded-full bg-transparent self-end"
                        >
                            {isUploading ? (
                                <div className="relative w-4 h-4">
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    {uploadProgress > 0 && (
                                        <div className="absolute -bottom-1 -right-1 text-xs font-bold">{uploadProgress}%</div>
                                    )}
                                </div>
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={voice.startRecording}
                            disabled={disabled}
                            className="text-muted-foreground text-[#E84B4B] flex-shrink-0 px-3 py-2 rounded-full dark:bg-[#0f172a]/20 bg-transparent  rounded-full backdrop-blur-[12px]  flex items-center justify-center  self-end  hover:shadow-[2px_2px_0_0_#E84B4B] transition-all duration-300"
                        >
                            <Mic className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.rtf,.md"
                onChange={handleFileSelect}
                className="hidden"
            />

            {hasError && <div className="mt-2 text-xs text-red-500">{uploadError || voice.error}</div>}
        </div>
    );
}
